import { useState, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useMixerStore } from '../store/mixerStore'
import Deck from '../components/Deck'
import Crossfader from '../components/Crossfader'
import EQControl from '../components/EQControl'
import LoadingSpinner from '../components/LoadingSpinner'
import { getStreamUrl, saveBlend } from '../services/api'
import './MixerPage.css'

function MixerPage() {
  const location = useLocation()
  const { deckA, deckB, setDeckA, setDeckB } = useMixerStore()

  const [audioContext] = useState(() => new (window.AudioContext || window.webkitAudioContext)())
  const [loading, setLoading] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [savingBlend, setSavingBlend] = useState(false)
  const [recommendations, setRecommendations] = useState([])
  const [sourceTrack, setSourceTrack] = useState(null)
  const [sourceAnalysis, setSourceAnalysis] = useState(null)

  const deckARef = useRef(null)
  const deckBRef = useRef(null)
  const hasLoadedFromState = useRef(false) // Track if we've already loaded from location.state

  useEffect(() => {
    // Only load ONCE when component mounts with location.state
    // Prevents infinite re-render loop from location.state object reference changes
    if (hasLoadedFromState.current) return
    if (!location.state) return

    hasLoadedFromState.current = true

    // Load Deck A (source track) if provided
    if (location.state.sourceTrack && location.state.sourceAnalysis) {
      setSourceTrack(location.state.sourceTrack)
      setSourceAnalysis(location.state.sourceAnalysis)
      setDeckA({
        track: location.state.sourceTrack,
        analysis: location.state.sourceAnalysis,
        cuePoint: 0
      })
    }

    // Load Deck B from navigation state if provided
    if (location.state.deckBTrack) {
      setDeckB({
        track: location.state.deckBTrack,
        analysis: location.state.deckBAnalysis,
        cuePoint: 0
      })
    }

    // Load recommendations if provided
    if (location.state.recommendations) {
      setRecommendations(location.state.recommendations)
    }
  }, [location.state, setDeckA, setDeckB])

  const playBothFromCues = async () => {
    if (!deckA || !deckB) {
      alert('Please load tracks into both decks')
      return
    }

    setLoading(true)
    try {
      console.log('[MIXER] Starting playback from cue points...')

      // Resume audio context if suspended
      if (audioContext.state === 'suspended') {
        console.log('[MIXER] Resuming AudioContext...')
        await audioContext.resume()
      }

      // Play both decks from their cue points
      const playPromises = []

      if (deckARef.current) {
        console.log('[MIXER] Playing Deck A from cue...')
        playPromises.push(deckARef.current.play())
      }

      if (deckBRef.current) {
        console.log('[MIXER] Playing Deck B from cue...')
        playPromises.push(deckBRef.current.play())
      }

      await Promise.all(playPromises)
      setIsPlaying(true)
      console.log('[MIXER] Both decks playing successfully')
    } catch (error) {
      console.error('[MIXER] Playback error:', error)
      alert(`Failed to play tracks: ${error.message}. Please wait for waveforms to load first.`)
    } finally {
      setLoading(false)
    }
  }

  const pauseBoth = () => {
    if (deckARef.current) {
      deckARef.current.pause()
    }
    if (deckBRef.current) {
      deckBRef.current.pause()
    }
    setIsPlaying(false)
  }

  const rewindToCues = () => {
    if (deckARef.current) {
      deckARef.current.rewindToCue()
    }
    if (deckBRef.current) {
      deckBRef.current.rewindToCue()
    }
    setIsPlaying(false)
  }

  const handleSaveBlend = async () => {
    if (!deckA || !deckB) {
      alert('Please load tracks into both decks')
      return
    }

    const blendName = prompt('Enter a name for this blend:')
    if (!blendName) return

    setSavingBlend(true)
    try {
      const blendData = {
        name: blendName,
        trackA: {
          id: deckA.track.id,
          title: deckA.track.title,
          artist: deckA.track.user?.username || deckA.track.artist,
          cuePoint: deckARef.current?.getCuePoint() || 0,
          eq: deckARef.current?.getEQSettings() || { low: 50, mid: 50, high: 50 }
        },
        trackB: {
          id: deckB.track.id,
          title: deckB.track.title,
          artist: deckB.track.user?.username || deckB.track.artist,
          cuePoint: deckBRef.current?.getCuePoint() || 0,
          eq: deckBRef.current?.getEQSettings() || { low: 50, mid: 50, high: 50 }
        }
      }

      await saveBlend(blendData)
      alert('Blend saved successfully!')
    } catch (error) {
      console.error('Save blend error:', error)
      alert('Failed to save blend. Please try again.')
    } finally {
      setSavingBlend(false)
    }
  }

  const handleLoadRecommendation = (recommendation) => {
    // Pause playback before swapping
    if (isPlaying) {
      pauseBoth()
    }

    // Load new track into Deck B
    setDeckB({
      track: recommendation.track,
      analysis: recommendation.analysis,
      cuePoint: 0
    })

    console.log(`[MIXER] Loaded recommendation into Deck B: ${recommendation.track.title}`)
  }

  const getEQSuggestion = () => {
    if (!deckA?.analysis || !deckB?.analysis) return null

    const aSubBass = deckA.analysis.subBassEnergy || 0
    const bSubBass = deckB.analysis.subBassEnergy || 0

    if (aSubBass > bSubBass + 0.2) {
      return {
        message: 'Track A has dominant sub-bass. Suggest cutting LOW on Track A.',
        cutDeck: 'A'
      }
    } else if (bSubBass > aSubBass + 0.2) {
      return {
        message: 'Track B has dominant sub-bass. Suggest cutting LOW on Track B.',
        cutDeck: 'B'
      }
    }

    return null
  }

  const eqSuggestion = getEQSuggestion()

  return (
    <div className="mixer-page">
      <div className="mixer-header">
        <h2>DJ Mixer</h2>
        <p>Set cue points, adjust EQ, and preview your blends</p>
      </div>

      {recommendations.length > 0 && (
        <div className="recommendations-panel">
          <h3 className="recommendations-title">Try These Blends</h3>
          <div className="recommendations-list">
            {recommendations.map((rec, index) => (
              <div
                key={rec.track.id || index}
                className={`recommendation-item ${deckB?.track?.id === rec.track.id ? 'active' : ''}`}
                onClick={() => handleLoadRecommendation(rec)}
              >
                <div className="rec-artwork">
                  <img
                    src={rec.track.artwork_url || '/placeholder-artwork.png'}
                    alt={rec.track.title}
                  />
                </div>
                <div className="rec-info">
                  <div className="rec-title">{rec.track.title}</div>
                  <div className="rec-artist">{rec.track.user?.username || rec.track.artist}</div>
                  <div className="rec-stats">
                    <span className="rec-bpm">{rec.analysis?.bpm} BPM</span>
                    <span className="rec-key">{rec.analysis?.key}</span>
                  </div>
                </div>
                <div className="rec-compatibility">
                  {rec.compatibility}%
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {eqSuggestion && (
        <div className="eq-suggestion">
          <span className="suggestion-icon">💡</span>
          <span className="suggestion-text">{eqSuggestion.message}</span>
        </div>
      )}

      <div className="decks-container">
        <div className="deck-wrapper">
          <h3>Deck A</h3>
          <Deck
            ref={deckARef}
            deckData={deckA}
            audioContext={audioContext}
            deckLabel="A"
          />
        </div>

        <div className="deck-wrapper">
          <h3>Deck B</h3>
          <Deck
            ref={deckBRef}
            deckData={deckB}
            audioContext={audioContext}
            deckLabel="B"
          />
        </div>
      </div>

      {/* Crossfader for mixing between Deck A and Deck B */}
      {deckA && deckB && (
        <Crossfader
          deckARef={deckARef}
          deckBRef={deckBRef}
        />
      )}

      <div className="master-controls">
        <button
          className="control-btn rewind"
          onClick={rewindToCues}
          disabled={!deckA || !deckB}
        >
          ◀◀ Rewind to Cues
        </button>

        <button
          className="control-btn play"
          onClick={playBothFromCues}
          disabled={!deckA || !deckB || loading || isPlaying}
        >
          {loading ? <LoadingSpinner size="small" /> : isPlaying ? '⏸ Pause' : '▶ Play Both'}
        </button>

        {isPlaying && (
          <button
            className="control-btn pause"
            onClick={pauseBoth}
          >
            ⏸ Pause
          </button>
        )}

        <button
          className="control-btn save"
          onClick={handleSaveBlend}
          disabled={!deckA || !deckB || savingBlend}
        >
          {savingBlend ? <LoadingSpinner size="small" /> : '💾 Save Blend'}
        </button>
      </div>

      {(!deckA || !deckB) && (
        <div className="empty-state">
          <p>Load tracks from the search page to start mixing</p>
        </div>
      )}
    </div>
  )
}

export default MixerPage
